import React, { useState } from 'react';
import { Eye, EyeOff, ExternalLink, Scale, Shield, Heart, CheckCircle2, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Hero Preview Component - reusable for all pages
export const HeroPreview = ({ title, highlight, description }) => (
  <div className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 py-8 px-4 rounded-xl">
    <div className="text-center">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
        {title} <span className="text-orange-500">{highlight}</span>
      </h1>
      <p className="text-sm text-gray-700 max-w-lg mx-auto">
        {description}
      </p>
    </div>
  </div>
);

// Services Preview Component
export const ServicesPreview = ({ content }) => {
  const serviceColors = [
    { bg: 'from-orange-50 to-orange-100', icon: 'bg-orange-500' },
    { bg: 'from-blue-50 to-blue-100', icon: 'bg-blue-500' },
    { bg: 'from-green-50 to-green-100', icon: 'bg-green-500' }
  ];
  const icons = [Scale, Shield, Heart];

  return (
    <div className="space-y-4">
      {/* Hero */}
      <HeroPreview 
        title={content.hero.title} 
        highlight={content.hero.highlight} 
        description={content.hero.description} 
      />

      {/* Intro */}
      <div className="bg-white p-4 rounded-xl border">
        <h2 className="text-lg font-bold text-gray-900 mb-2 text-center">{content.intro.heading}</h2>
        <p className="text-sm text-gray-600 text-center">{content.intro.body_markdown}</p>
      </div>

      {/* Services Cards */}
      <div className="space-y-3">
        {(content.services.items || []).slice(0, 3).map((service, idx) => {
          const Icon = icons[idx] || Scale;
          const colors = serviceColors[idx] || serviceColors[0];
          return (
            <div key={idx} className={`bg-gradient-to-br ${colors.bg} rounded-xl p-4`}>
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center`}>
                  <Icon className="text-white" size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{service.title}</h3>
                  <p className="text-xs text-gray-700">{service.body_markdown}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-center">
        <h2 className="text-lg font-bold text-white mb-2">{content.cta.heading}</h2>
        <p className="text-xs text-orange-100 mb-3">{content.cta.body_markdown}</p>
        <span className="inline-flex items-center px-4 py-2 bg-white text-orange-600 rounded-lg text-sm font-bold">
          {content.cta.button_text}
          <ArrowRight className="ml-1" size={14} />
        </span>
      </div>
    </div>
  );
};

// About Preview Component
export const AboutPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Mission & Vision */}
    <div className="grid grid-cols-2 gap-3">
      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
        <h3 className="font-bold text-gray-900 mb-2">Our Mission</h3>
        <p className="text-xs text-gray-700">{content.mission?.text}</p>
      </div>
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
        <h3 className="font-bold text-gray-900 mb-2">Our Vision</h3>
        <p className="text-xs text-gray-700">{content.vision?.text}</p>
      </div>
    </div>

    {/* Values */}
    {content.values && (
      <div className="bg-white rounded-xl p-4 border">
        <h3 className="font-bold text-gray-900 mb-3 text-center">Core Values</h3>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-orange-50 rounded-lg">
            <p className="font-semibold text-sm text-orange-600">{content.values.integrity_title}</p>
            <p className="text-xs text-gray-600 mt-1">{content.values.integrity_text?.slice(0, 50)}...</p>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <p className="font-semibold text-sm text-blue-600">{content.values.unity_title}</p>
            <p className="text-xs text-gray-600 mt-1">{content.values.unity_text?.slice(0, 50)}...</p>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="font-semibold text-sm text-green-600">{content.values.excellence_title}</p>
            <p className="text-xs text-gray-600 mt-1">{content.values.excellence_text?.slice(0, 50)}...</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Membership Preview Component
export const MembershipPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Benefits */}
    <div className="bg-white rounded-xl p-4 border">
      <h3 className="font-bold text-gray-900 mb-3 text-center">{content.benefits?.heading || 'Membership Benefits'}</h3>
      <div className="grid grid-cols-3 gap-2">
        {(content.benefits?.items || []).slice(0, 3).map((item, idx) => {
          const colors = ['text-orange-500', 'text-blue-500', 'text-green-500'];
          const bgs = ['bg-orange-50', 'bg-blue-50', 'bg-green-50'];
          return (
            <div key={idx} className={`text-center p-3 ${bgs[idx]} rounded-lg`}>
              <CheckCircle2 className={`${colors[idx]} mx-auto mb-2`} size={24} />
              <p className="font-semibold text-sm text-gray-900">{item.title}</p>
              <p className="text-xs text-gray-600 mt-1">{item.body_markdown?.slice(0, 40)}...</p>
            </div>
          );
        })}
      </div>
    </div>

    {/* Form Preview */}
    <div className="bg-white rounded-xl p-4 border text-center">
      <h3 className="font-bold text-gray-900 mb-1">{content.form?.heading || 'Apply for Membership'}</h3>
      <p className="text-xs text-gray-600">{content.form?.subheading}</p>
      <div className="mt-3 space-y-2">
        <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
        <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
        <div className="h-10 bg-orange-200 rounded"></div>
      </div>
    </div>
  </div>
);

// Contact Preview Component
export const ContactPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Form Preview */}
    <div className="bg-white rounded-xl p-4 border">
      <h3 className="font-bold text-gray-900 mb-1">{content.form?.heading || 'Send us a Message'}</h3>
      <p className="text-xs text-gray-600 mb-3">{content.form?.subheading}</p>
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="h-8 bg-gray-100 rounded"></div>
          <div className="h-8 bg-gray-100 rounded"></div>
        </div>
        <div className="h-8 bg-gray-100 rounded"></div>
        <div className="h-16 bg-gray-100 rounded"></div>
        <div className="h-10 bg-orange-200 rounded"></div>
      </div>
    </div>
  </div>
);

// Affiliates Preview Component
export const AffiliatesPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Intro */}
    <div className="bg-white p-4 rounded-xl border text-center">
      <h2 className="text-lg font-bold text-gray-900 mb-2">{content.intro?.heading}</h2>
      <p className="text-sm text-gray-600">{content.intro?.body_markdown}</p>
    </div>

    {/* Benefits */}
    <div className="bg-white p-4 rounded-xl border">
      <h3 className="font-bold text-gray-900 mb-3 text-center">{content.benefits?.heading}</h3>
      <div className="space-y-2">
        {(content.benefits?.items || []).map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
            <CheckCircle2 className="text-orange-500 flex-shrink-0" size={16} />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>

    {/* CTA */}
    <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-center">
      <h2 className="text-lg font-bold text-white mb-2">{content.cta?.heading}</h2>
      <p className="text-xs text-blue-100 mb-3">{content.cta?.body_markdown}</p>
      <span className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-bold">
        {content.cta?.button_text}
      </span>
    </div>
  </div>
);

// Events Preview Component
export const EventsPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Empty State */}
    <div className="bg-white p-6 rounded-xl border text-center">
      <p className="text-gray-500 font-medium">{content.empty?.heading}</p>
      <p className="text-xs text-gray-400 mt-1">{content.empty?.body_markdown}</p>
    </div>
  </div>
);

// Resources Preview Component
export const ResourcesPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Empty State */}
    <div className="bg-white p-6 rounded-xl border text-center">
      <p className="text-gray-500 font-medium">{content.empty?.heading}</p>
      <p className="text-xs text-gray-400 mt-1">{content.empty?.body_markdown}</p>
    </div>
  </div>
);

// News Preview Component
export const NewsPreview = ({ content }) => (
  <div className="space-y-4">
    {/* Hero */}
    <HeroPreview 
      title={content.hero.title} 
      highlight={content.hero.highlight} 
      description={content.hero.description} 
    />

    {/* Sample Cards */}
    <div className="grid grid-cols-3 gap-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-lg border overflow-hidden">
          <div className="h-16 bg-gradient-to-br from-orange-300 to-orange-500"></div>
          <div className="p-2">
            <div className="h-3 bg-gray-200 rounded mb-1"></div>
            <div className="h-2 bg-gray-100 rounded w-3/4"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Main Preview Panel Wrapper
const LivePreviewPanel = ({ children, pageName, showPreview, setShowPreview }) => {
  const previewUrl = `${window.location.origin.replace('/admin', '')}/${pageName === 'home' ? '' : pageName}`;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <Button
          type="button"
          variant={showPreview ? "default" : "outline"}
          onClick={() => setShowPreview(!showPreview)}
          className={showPreview ? "bg-blue-500 hover:bg-blue-600" : ""}
          data-testid="toggle-preview-btn"
        >
          {showPreview ? <EyeOff className="mr-2" size={18} /> : <Eye className="mr-2" size={18} />}
          {showPreview ? 'Hide Preview' : 'Show Live Preview'}
        </Button>
        
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600"
        >
          <ExternalLink size={16} className="mr-1" />
          Open in New Tab
        </a>
      </div>

      {showPreview && (
        <div className="border-2 border-blue-200 rounded-xl p-4 bg-gray-50 mb-6">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-100">
            <div className="w-3 h-3 rounded-full bg-red-400"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
            <span className="text-xs text-gray-500 ml-2">Live Preview</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export default LivePreviewPanel;
